import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { integrationDisconnect } from '../../actions/Auth';
import { refreshCollections } from '../../actions/Collections';
import { refreshFiles } from '../../actions/Files';

import {
  APIgetImporter,
  APIcreateImport,
  APIhideImport,
  APIretryImport,
} from '../../api/Importers';

import ImporterModalHeader from './ModalHeader';
import ImporterModalConnect from './ModalConnect';
import ImporterModalBrowser from './ModalBrowser';
import ImporterModalAccountMenu from './ModalAccountMenu';
import ImporterModalCollection from './ModalCollection';
import ImporterModalConfirmation from './ModalConfirmation';
import ImporterModalImportList from './ModalImportList';
import ImporterModalError from './ModalError';
import ProcessingSpinner from '../ProcessingSpinner';

import { sendTrackingEvent } from '../../helpers';

import ErrorBoundary from '../ErrorBoundary';
import Loading from '../Loading';

const ImporterModal = ({
  dispatch,
  history,
  location,
  profile,
  currentCollection,
  modalOpen,
  serviceId,
  handleClose,
}) => {
  const intl = useIntl();

  const [mode, setMode] = useState(null);
  const [importer, setImporter] = useState(null);
  const [importerLoading, setImporterLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedCollectionError, setSelectedCollectionError] = useState(false);
  const [importCreating, setImportCreating] = useState(false);
  const [importError, setImportError] = useState(false);
  const [importData, setImportData] = useState(null);

  const importerProfile =
    profile.importers ? profile.importers.find(i => i.id === serviceId) : null;

  useEffect(() => {
    if (serviceId) {
      sendTrackingEvent('import-modal-open', { service: serviceId });
    }
  }, [serviceId]);

  useEffect(() => {
    if (
      modalOpen &&
      !selectedCollection &&
      currentCollection?.open
    ) {
      setSelectedCollection(currentCollection);
    }
  }, [modalOpen, currentCollection, selectedCollection]);

  useEffect(() => {
    if (mode && mode !== 'disconnect') {
      return;
    }

    if (!serviceId) {
      return;
    }

    setImportError(false);
    setImporterLoading(true);
    APIgetImporter(serviceId).then((response) => {
      const isImporterConnected = response.is_connected && response.account;
      setImporter({
        ...response,
        connected: isImporterConnected,
      });
      if (isImporterConnected) {
        if (serviceId === 'googledrive' && Object.keys(response.account).length === 0) {
          setImportError(true);
        } else {
          setMode('browse');
        }
      } else {
        setMode('connect');
      }
      setImporterLoading(false);
    }).catch(() => {
      setImportError(true);
      setImporterLoading(false);
    });
  }, [serviceId, mode]);

  const handleCloseAndCleanUrl = () => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.delete('importer');
    let urlParamsString = urlParams.toString();
    if (urlParams.entries().length > 0) {
      urlParamsString = `?${urlParamsString}`;
    }
    history.replace(`${location.pathname}${urlParamsString}`);

    if (currentCollection && currentCollection.uuid === selectedCollection.uuid) {
      dispatch(refreshCollections());
      dispatch(refreshFiles());
    }

    setMode(null);
    setImporter(null);
    setSelectedCollection(null);
    setSelectedItems([]);

    handleClose();
  };

  const handleRedirectToAuthUrl = () => {
    window.location = importer.auth_url;
  };

  const handleDisconnect = () => {
    if (importCreating) {
      return;
    }

    dispatch(integrationDisconnect(serviceId)).then(() => {
      setMode('disconnect');
    });
  };

  const handleSetSelectedCollection = (collection) => {
    setSelectedCollection(collection);
    setSelectedCollectionError(false);
  };

  const handleCreateImport = () => {
    if (!selectedCollection) {
      setSelectedCollectionError(true);
      return;
    }
    setSelectedCollectionError(false);

    setImportError(false);
    setImportCreating(true);
    APIcreateImport(serviceId, {
      items: selectedItems,
      collection_uuid: selectedCollection ? selectedCollection.uuid : null,
    }).then((response) => {
      if (response.uuid) {
        setImportData(response);
        setMode('confirmation');
        sendTrackingEvent('import-started', {
          service: serviceId,
          importUuid: response.uuid,
        });
      }
      setImportCreating(false);
    }).catch(() => {
      setImportError(true);
      setImportCreating(false);
    });
  };

  const handleRetryImport = importUuid => (
    APIretryImport(importUuid)
  );

  const handleHideImport = importUuid => (
    APIhideImport(importUuid)
  );

  const resetImportState = () => {
    setMode(null);
    setSelectedCollection(null);
    setSelectedItems([]);
  };

  return (
    <Modal
      className="modal-import modal-xl"
      isOpen={modalOpen}
      toggle={handleCloseAndCleanUrl}
    >
      <ErrorBoundary>
        {(importer || importerProfile) && (
          <ImporterModalHeader
            importer={importer || importerProfile}
            handleClose={handleCloseAndCleanUrl}
          />
        )}

        <ModalBody className={`modal-import-body-${mode}`}>
          <ImporterModalImportList
            importer={importer}
            handleRetryImport={handleRetryImport}
            handleHideImport={handleHideImport}
          />
          {importerLoading ? (
            <Loading />
          ) : (
            <>
              {importError && (
                <ImporterModalError importer={importer} />
              )}
              {mode === 'connect' && (
                <ImporterModalConnect
                  importer={importer}
                  handleRedirectToAuthUrl={handleRedirectToAuthUrl}
                />
              )}
              {mode === 'browse' && (
                <ImporterModalBrowser
                  importer={importer}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                />
              )}
              {mode === 'confirmation' && (
                <ImporterModalConfirmation
                  importer={importer}
                  importData={importData}
                  resetImportState={resetImportState}
                />
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter className="justify-sb">
          <ImporterModalAccountMenu
            importer={importer}
            handleDisconnect={handleDisconnect}
          />
          <div className="importer-button-group button-group">
            {(importer && importer.connected && mode !== 'confirmation' && !importError) && (
              <ImporterModalCollection
                selectedCollection={selectedCollection}
                selectedCollectionError={selectedCollectionError}
                setSelectedCollection={handleSetSelectedCollection}
              />
            )}
            <div className="button-group">
              <Button
                color="secondary"
                disabled={importCreating}
                onClick={handleCloseAndCleanUrl}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              {(importer && importer.connected && mode !== 'confirmation' && !importError) && (
                <Button
                  color="primary"
                  disabled={selectedItems.length === 0 || importCreating}
                  onClick={handleCreateImport}
                >
                  <ProcessingSpinner show={importCreating} />
                  {intl.formatMessage({ defaultMessage: 'Import' })}
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    </Modal>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
  currentCollection: state.Collections.collection,
});

ImporterModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  currentCollection: PropTypes.object,
  modalOpen: PropTypes.bool.isRequired,
  serviceId: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
};

ImporterModal.defaultProps = {
  currentCollection: null,
  serviceId: null,
};

export default withRouter(connect(mapStateToProps)(ImporterModal));
